import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const TEXTURE =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_cosecha-eventos/artifacts/xs7qt8ma_DJDJDJ.jpg";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Bienvenido de nuevo");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2 bg-background">
      {/* Texture panel */}
      <div className="relative hidden overflow-hidden lg:block grain">
        <img src={TEXTURE} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30" />
        <div className="absolute bottom-0 left-0 p-12">
          <p className="overline text-primary">Cosecha Hashem Adonay</p>
          <h2 className="mt-4 font-serif text-5xl leading-none text-foreground">
            Administración del<br />Ministerio
          </h2>
          <p className="mt-4 max-w-sm text-sm text-muted-foreground">
            Gestiona eventos y finanzas con reverencia y orden.
          </p>
        </div>
      </div>

      {/* Form panel */}
      <div className="relative flex items-center justify-center px-6 py-12">
        <Link
          to="/"
          data-testid="back-home-link"
          className="absolute left-6 top-6 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-primary"
        >
          <ArrowLeft className="h-4 w-4" /> Inicio
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-sm"
        >
          <div className="mb-8 flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full border border-primary/40 text-primary">
              <Lock className="h-5 w-5" />
            </div>
            <div>
              <p className="overline text-primary">Acceso privado</p>
              <h1 className="font-serif text-3xl leading-none">Iniciar Sesión</h1>
            </div>
          </div>

          <form onSubmit={onSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Correo electrónico</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@cosecha.com"
                required
                className="h-11 bg-muted"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="h-11 bg-muted"
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="h-11 w-full rounded-full bg-primary text-primary-foreground hover:bg-[#E5B54B]"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entrar al panel"}
            </Button>
          </form>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            Panel exclusivo para administradores de la iglesia.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
